import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { http } from "@/lib/api";
import {
  Wind, Thermometer, Drop, Sun, MapPin, Crosshair, MagnifyingGlass, Warning,
  Leaf, Waves, ArrowRight, Sparkle, Plus, Star, X,
} from "@phosphor-icons/react";
import EnvironmentalStatus, {
  aqiToSeverity, uvToSeverity,
} from "@/components/EnvironmentalStatus";
import InviteFriends from "@/components/InviteFriends";
import PulseMap from "@/components/PulseMap";
import PushToggle from "@/components/PushToggle";
import WhatsHappening from "@/components/WhatsHappening";
import ShareButton from "@/components/ShareButton";
import FeelThePulseIntro from "@/components/FeelThePulseIntro";

// --- Persistence -------------------------------------------------------------
const LOC_KEY = "np:location:v1";
const SAVED_KEY = "np:saved-locations:v1";

const DEFAULT_LOC = { name: "New Delhi, India", lat: 28.6139, lon: 77.2090, source: "default" };

function loadLocation() {
  try {
    const raw = localStorage.getItem(LOC_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return DEFAULT_LOC;
}
function saveLocation(loc) {
  try { localStorage.setItem(LOC_KEY, JSON.stringify(loc)); } catch (e) {}
}
function loadSavedList() {
  try { return JSON.parse(localStorage.getItem(SAVED_KEY) || "[]"); } catch { return []; }
}
function saveList(list) {
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(list.slice(0, 8))); } catch {}
}

// --- Small helpers -----------------------------------------------------------
function toLocalTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}
function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Good night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
}
function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s1 = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s1));
}
const WEATHER_CODE = {
  0: "Clear", 1: "Mostly clear", 2: "Partly cloudy", 3: "Overcast",
  45: "Foggy", 48: "Rime fog", 51: "Light drizzle", 53: "Drizzle", 55: "Heavy drizzle",
  61: "Light rain", 63: "Rain", 65: "Heavy rain", 71: "Light snow", 73: "Snow", 75: "Heavy snow",
  80: "Rain showers", 81: "Heavy showers", 82: "Violent showers",
  95: "Thunderstorm", 96: "Storm w/ hail", 99: "Severe storm",
};

// A rotating library of small, testable eco-actions (Master Prompt §35, §37).
const ECO_ACTIONS = [
  { title: "Skip the plastic bottle today", body: "Carry a refillable one — you'll avoid ~50 g of single-use plastic." },
  { title: "Take a 5-minute cooler shower", body: "Save roughly 40 L of water and the energy to heat it." },
  { title: "Unplug one always-on device", body: "Phantom loads add up. A single set-top box wastes ~90 kWh a year." },
  { title: "Walk a trip you'd normally drive", body: "Cars produce ~192 g CO₂ per km. Small trips add up fast." },
  { title: "Cook one plant-based meal", body: "Even one meat-free meal a week meaningfully lowers dietary footprint." },
  { title: "Photograph one wild thing near you", body: "Add it to iNaturalist — citizen science shapes real conservation." },
  { title: "Repair, don't replace", body: "Extending a phone's life by a year cuts its footprint by nearly a third." },
];
function actionOfDay() {
  const d = new Date();
  const doy = Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000);
  return ECO_ACTIONS[doy % ECO_ACTIONS.length];
}

// --- Location picker --------------------------------------------------------
function LocationPicker({ onPick, current, saved, onSave, onRemove }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [permError, setPermError] = useState("");

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setPermError("");
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=5&addressdetails=1`
      );
      const j = await r.json();
      setResults(j.map((x) => ({
        name: x.display_name.split(",").slice(0, 3).join(",").trim(),
        lat: parseFloat(x.lat), lon: parseFloat(x.lon), source: "search",
      })));
    } catch {
      setPermError("Search unavailable.");
    } finally { setBusy(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return setPermError("Geolocation not supported here.");
    setBusy(true); setPermError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lon } = pos.coords;
        // Reverse-geocode via our backend for a friendly name (also caches).
        let name = `${lat.toFixed(3)}, ${lon.toFixed(3)}`;
        try {
          const r = await http.post("/geocode/reverse", { lat, lon });
          const d = r.data || {};
          name = [d.city, d.state, d.country].filter(Boolean).join(", ") || name;
        } catch {}
        onPick({ name, lat, lon, source: "gps" });
        setBusy(false);
      },
      (err) => {
        setBusy(false);
        setPermError(err.code === 1 ? "Permission denied. You can still search by name." : "Couldn't get your location.");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  };

  return (
    <div data-testid="pulse-locpicker" className="space-y-4">
      <div className="text-sm text-[color:var(--np-text-2,#3A3830)]">
        Show environmental conditions near you. Location is optional and stored only on this device.
      </div>
      <button
        data-testid="pulse-use-location"
        onClick={useMyLocation}
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:opacity-90 disabled:opacity-50"
      >
        <Crosshair size={14} weight="bold" />
        {busy ? "Locating…" : "Use my location"}
      </button>
      <form onSubmit={search} className="flex gap-2">
        <div className="flex-1 relative">
          <MagnifyingGlass size={14} weight="thin" className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--np-muted)]" />
          <input
            data-testid="pulse-search-input"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search city or town…"
            className="w-full bg-transparent border border-black/20 focus:border-[color:var(--np-brand,#B85F2A)] outline-none pl-9 pr-3 py-2 font-mono-tech text-xs tracking-[0.1em]"
          />
        </div>
        <button
          type="submit"
          data-testid="pulse-search-submit"
          className="font-mono-tech text-[10px] tracking-[0.28em] uppercase px-4 py-2 border border-[#1B4332] hover:bg-[#1B4332] hover:text-[#F5EBDC] transition-colors"
        >
          Go
        </button>
      </form>
      {permError && <div className="text-xs text-red-700">{permError}</div>}
      {results.length > 0 && (
        <ul className="space-y-1" data-testid="pulse-search-results">
          {results.map((r, i) => (
            <li key={i}>
              <button
                onClick={() => { onPick({ ...r }); setResults([]); setQ(""); }}
                className="w-full text-left px-3 py-2 border border-black/10 hover:border-[color:var(--np-brand,#B85F2A)] flex items-start gap-2"
              >
                <MapPin size={14} weight="fill" className="mt-1 text-[color:var(--np-brand,#B85F2A)]" />
                <div className="min-w-0">
                  <div className="text-sm truncate">{r.name}</div>
                  <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted)]">
                    {r.lat.toFixed(3)}, {r.lon.toFixed(3)}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {saved.length > 0 && (
        <div>
          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted)] mb-2">Saved</div>
          <ul className="space-y-1">
            {saved.map((s, i) => (
              <li key={i} className="flex items-center gap-2">
                <button
                  onClick={() => onPick(s)}
                  className="flex-1 text-left px-3 py-2 border border-black/10 hover:border-[color:var(--np-brand,#B85F2A)] flex items-center gap-2"
                >
                  <Star size={12} weight="fill" className="text-[#E4A15C]" />
                  <span className="text-sm truncate">{s.name}</span>
                </button>
                <button
                  onClick={() => onRemove(s)}
                  className="p-2 opacity-60 hover:opacity-100"
                  aria-label={`Remove ${s.name}`}
                ><X size={12} /></button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        data-testid="pulse-save-location"
        onClick={() => onSave(current)}
        className="w-full inline-flex items-center justify-center gap-2 border border-black/20 px-4 py-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-[#1B4332] hover:text-[#F5EBDC]"
      >
        <Plus size={12} weight="bold" />
        Save {current.name}
      </button>
    </div>
  );
}

// --- Main page --------------------------------------------------------------
export default function Pulse() {
  const [loc, setLoc] = useState(loadLocation);
  const [saved, setSaved] = useState(loadSavedList);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const action = useMemo(actionOfDay, []);

  useEffect(() => { saveLocation(loc); }, [loc]);
  useEffect(() => { saveList(saved); }, [saved]);

  // Fetch weather + AQI for current location
  useEffect(() => {
    let alive = true;
    setLoading(true);
    http.get(`/weather?lat=${loc.lat}&lon=${loc.lon}&name=${encodeURIComponent(loc.name)}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [loc.lat, loc.lon, loc.name]);

  // Nearest civic hazard (existing endpoint)
  useEffect(() => {
    let alive = true;
    http.get(`/civic-reports?limit=100`).then((r) => {
      if (!alive) return;
      const list = (r.data?.reports || [])
        .filter((rep) => rep.lat != null && rep.lon != null)
        .map((rep) => ({ ...rep, distance_km: haversineKm(loc, rep) }))
        .sort((a, b) => a.distance_km - b.distance_km)
        .slice(0, 3);
      setAlerts(list);
    }).catch(() => alive && setAlerts([]));
    return () => { alive = false; };
  }, [loc.lat, loc.lon]);

  const cur = data?.weather?.current;
  const daily = data?.weather?.daily;
  const aqi = data?.air_quality?.current?.us_aqi;
  const code = cur?.weather_code;
  const desc = code != null ? WEATHER_CODE[code] || "" : "";
  const updated = cur?.time || data?.timestamp || null;

  const onPick = (l) => { setLoc(l); setPickerOpen(false); };
  const onSave = (l) => {
    if (!l) return;
    setSaved((prev) => prev.some((x) => x.name === l.name) ? prev : [l, ...prev].slice(0, 8));
  };
  const onRemove = (l) => setSaved((prev) => prev.filter((x) => x.name !== l.name));

  return (
    <main className="bg-[#F5EBDC] text-[#1B4332] pt-32 md:pt-36 pb-10 min-h-screen">
      {/* HEADER */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mb-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-1">
              / NaturePulse · {greeting()}
            </div>
            <h1 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              Your local nature pulse.
            </h1>
            <button
              data-testid="pulse-open-picker"
              onClick={() => setPickerOpen(true)}
              className="mt-2 inline-flex items-center gap-1.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]"
            >
              <MapPin size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
              {loc.name}
              <span className="opacity-60">· change</span>
            </button>
            <div className="mt-3">
              <ShareButton
                title={`My local pulse — ${loc.name}`}
                text={`Air, weather and hazards for ${loc.name} on NaturePulse.`}
                hashtags={["NaturePulse", "AirQuality"]}
                variant="chip"
                testid="pulse-share"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FEEL THE PULSE OF NATURE — poetic intro linking to the full story */}
      <FeelThePulseIntro />

      {/* WHAT'S HAPPENING AROUND YOU? — Observation Network entry point */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mb-6">
        <WhatsHappening variant="hero" />
      </section>

      {/* LOCAL DASHBOARD */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <EnvironmentalStatus
            testid="pulse-card-aqi"
            label="Air Quality"
            value={aqi ?? "—"}
            unit="AQI"
            severity={aqiToSeverity(aqi)}
            hint={aqi != null && aqi <= 50 ? "Great day to be outside." : aqi != null && aqi <= 100 ? "Sensitive groups take it easy." : aqi != null ? "Consider limiting outdoor exertion." : "Awaiting live data."}
            source="Open-Meteo AQI"
            updatedAt={updated}
            icon={<Wind size={16} weight="fill" />}
          />
          <EnvironmentalStatus
            testid="pulse-card-temp"
            label="Temperature"
            value={cur?.temperature_2m != null ? Math.round(cur.temperature_2m) : "—"}
            unit="°C"
            severity={cur?.temperature_2m == null ? "unknown" : cur.temperature_2m >= 40 ? "severe" : cur.temperature_2m >= 35 ? "high" : cur.temperature_2m >= 30 ? "poor" : cur.temperature_2m >= 15 ? "good" : "moderate"}
            hint={desc}
            source="Open-Meteo"
            updatedAt={updated}
            icon={<Thermometer size={16} weight="fill" />}
          />
          <EnvironmentalStatus
            testid="pulse-card-humidity"
            label="Humidity"
            value={cur?.relative_humidity_2m ?? "—"}
            unit="%"
            severity={cur?.relative_humidity_2m == null ? "unknown" : cur.relative_humidity_2m >= 80 ? "poor" : "good"}
            source="Open-Meteo"
            updatedAt={updated}
            icon={<Drop size={16} weight="fill" />}
          />
          <EnvironmentalStatus
            testid="pulse-card-uv"
            label="UV Index"
            value={cur?.uv_index != null ? Number(cur.uv_index).toFixed(1) : "—"}
            severity={uvToSeverity(cur?.uv_index)}
            hint={cur?.uv_index != null && cur.uv_index >= 6 ? "Wear SPF if you're outside." : undefined}
            source="Open-Meteo"
            updatedAt={updated}
            icon={<Sun size={16} weight="fill" />}
          />
        </div>

        {/* Today at a glance */}
        <div className="mt-4 rounded-xl border border-black/10 bg-white/60 backdrop-blur p-4 md:p-5 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted)]">Today</div>
            <div className="font-serif-display text-2xl md:text-3xl mt-0.5">
              {daily?.temperature_2m_max?.[0] != null
                ? `${Math.round(daily.temperature_2m_min?.[0] ?? 0)}° — ${Math.round(daily.temperature_2m_max[0])}°`
                : "—"}
            </div>
            {loading && (
              <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted)] mt-1">Loading…</div>
            )}
          </div>
          <div className="flex items-center gap-4 text-xs md:text-sm">
            <div>
              <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted)]">Sunrise</div>
              <div>{toLocalTime(daily?.sunrise?.[0])}</div>
            </div>
            <div>
              <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted)]">Sunset</div>
              <div>{toLocalTime(daily?.sunset?.[0])}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ENVIRONMENTAL ALERTS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">/ Alerts near you</h2>
          <div className="flex items-center gap-3">
            <PushToggle />
            <Link to="/civic-reports" className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]">
              See all <ArrowRight size={10} weight="bold" className="inline -mt-0.5" />
            </Link>
          </div>
        </div>
        {alerts.length === 0 ? (
          <div data-testid="pulse-alerts-empty" className="rounded-xl border border-dashed border-black/20 p-6 text-center text-sm text-[color:var(--np-text-2)]">
            No active environmental hazards near {loc.name}. Nice.
          </div>
        ) : (
          <ul data-testid="pulse-alerts" className="space-y-2">
            {alerts.map((a) => (
              <li
                key={a.report_id}
                className="rounded-xl border border-black/10 bg-white/60 backdrop-blur p-3.5 flex items-start gap-3"
              >
                <span
                  className="mt-0.5 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full"
                  style={{ background: a.severity === "severe" ? "rgba(127,29,29,.15)" : a.severity === "high" ? "rgba(239,68,68,.15)" : "rgba(234,179,8,.15)" }}
                >
                  <Warning size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">{a.severity}</span>
                    <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted)]">· {a.hazard} · {Math.round(a.distance_km)} km</span>
                  </div>
                  <div className="font-serif-display text-base md:text-lg leading-tight mt-1 truncate">
                    {a.location}
                  </div>
                  <div className="text-xs md:text-sm text-[color:var(--np-text-2)] line-clamp-2 mt-0.5">{a.description}</div>
                  <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted)] mt-1">
                    Source: {a.reporter_name}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* PULSE MAP */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">/ Pulse map · toggle layers</h2>
          <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted)]">
            Air · Fires · Hazards
          </div>
        </div>
        <PulseMap center={{ lat: loc.lat, lon: loc.lon }} />
      </section>

      {/* TODAY'S ECO ACTION */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8">
        <div
          data-testid="pulse-eco-action"
          className="rounded-2xl p-5 md:p-6 text-[#F5EBDC] relative overflow-hidden"
          style={{ background: "linear-gradient(135deg,#1B3A2A 0%,#2E5A3A 55%,#5C7A3C 100%)" }}
        >
          <div className="absolute -right-4 -top-4 opacity-15">
            <Leaf size={140} weight="fill" />
          </div>
          <div className="relative">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#E4A15C]">
              <Sparkle size={12} weight="fill" className="inline -mt-0.5 mr-1" />
              Today&apos;s Eco Action
            </div>
            <h3 className="font-serif-display text-2xl md:text-3xl mt-2 leading-tight">{action.title}</h3>
            <p className="mt-2 text-sm md:text-base opacity-90 max-w-xl">{action.body}</p>
            <Link
              to="/challenges"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#F5EBDC] text-[#1B4332] px-4 py-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:opacity-90"
            >
              Start a 7-day challenge <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* GLOBAL PULSE — quick strip */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8 mb-4">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">/ Global pulse</h2>
          <Link to="/climate-change" className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]">
            Full report <ArrowRight size={10} weight="bold" className="inline -mt-0.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Climate anomaly", value: "+1.54", unit: "°C", hint: "vs 1850–1900", source: "WMO 2024" },
            { label: "CO₂", value: "424.6", unit: "ppm", hint: "monthly avg", source: "NOAA" },
            { label: "Sea level", value: "+101.2", unit: "mm", hint: "since 1993", source: "NASA JPL" },
            { label: "Forest loss", value: "3.7", unit: "Mha", hint: "primary forest 2023", source: "Global Forest Watch" },
          ].map((g) => (
            <div key={g.label} className="rounded-xl border border-black/10 bg-white/60 backdrop-blur p-4">
              <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted)]">{g.label}</div>
              <div className="mt-1 flex items-baseline gap-1">
                <div className="font-serif-display text-2xl md:text-3xl">{g.value}</div>
                <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted)]">{g.unit}</div>
              </div>
              <div className="text-xs text-[color:var(--np-text-2)]">{g.hint}</div>
              <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted)] mt-2">{g.source}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INVITE STRIP */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-6 mb-4">
        <div className="rounded-2xl border border-black/10 bg-gradient-to-br from-[#FBF2E1] to-[#EFDEC2] p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">/ Grow the pulse</div>
            <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mt-1">Every neighbour on the pulse matters.</h3>
            <p className="text-sm text-[#3A3830] mt-1 max-w-md">A single share often ends with a beach cleanup or a tree planted.</p>
          </div>
          <InviteFriends variant="full" label="Invite someone who cares about the planet" />
        </div>
      </section>

      {/* Picker sheet */}
      <AnimatePresence>
        {pickerOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setPickerOpen(false)}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            />
            <motion.div
              key="sheet"
              data-testid="pulse-picker-sheet"
              className="fixed inset-x-0 bottom-0 md:bottom-auto md:top-16 md:right-6 md:left-auto md:w-[420px] z-50 bg-[color:var(--np-bg,#F5EBDC)] rounded-t-2xl md:rounded-2xl border border-black/15 p-5 shadow-[0_-20px_60px_rgba(0,0,0,.25)]"
              initial={{ y: 400, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 400, opacity: 0 }}
              transition={{ type: "spring", stiffness: 240, damping: 26 }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Change location</div>
                <button onClick={() => setPickerOpen(false)} aria-label="Close" data-testid="pulse-picker-close">
                  <X size={16} weight="bold" />
                </button>
              </div>
              <LocationPicker
                current={loc}
                saved={saved}
                onPick={onPick}
                onSave={onSave}
                onRemove={onRemove}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </main>
  );
}
